// Public invoice landing page — recipient sees details + pays via Stripe.
// Mounted at /invoice/:token. No auth required.

import { useEffect, useState } from 'react';
import { Receipt, Download, CreditCard, AlertCircle, CheckCircle, Calendar } from 'lucide-react';
import { viewInvoicePublic, payInvoicePublic, invoicePdfPublicUrl } from '../api.js';

function fmt$(n) {
  const v = typeof n === 'number' ? n : parseFloat(n);
  if (!isFinite(v)) return '$0.00';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function fmtDate(d) {
  if (!d) return '';
  try {
    const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
    return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return ''; }
}

const STATUS_BADGE = {
  DRAFT:     { label: 'Draft',     bg: 'rgba(122,163,192,.12)', color: '#7aa3c0' },
  SENT:      { label: 'Sent',      bg: 'rgba(0,212,255,.12)',   color: '#00d4ff' },
  PARTIAL:   { label: 'Partial',   bg: 'rgba(255,183,0,.12)',   color: '#ffb700' },
  PAID:      { label: 'Paid',      bg: 'rgba(0,255,136,.12)',   color: '#00ff88' },
  OVERDUE:   { label: 'Overdue',   bg: 'rgba(255,61,107,.12)',  color: '#ff3d6b' },
  CANCELLED: { label: 'Cancelled', bg: 'rgba(100,116,139,.12)', color: '#64748b' },
};

export function InvoicePortalLandingPage({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [paying, setPaying] = useState(false);

  // Recover from a successful Stripe redirect by re-fetching after a short delay.
  // The webhook may take a second or two to process the payment.
  const wasPaid = typeof window !== 'undefined' && /[?&]invoice_paid=/.test(window.location.search);

  useEffect(() => {
    setLoading(true);
    viewInvoicePublic(token)
      .then(setData)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  // After Stripe success redirect, poll once (after a short delay) to pick up
  // the webhook's PAID status update.
  useEffect(() => {
    if (!wasPaid) return;
    const id = setTimeout(() => {
      viewInvoicePublic(token).then(setData).catch(() => {});
    }, 2000);
    return () => clearTimeout(id);
  }, [wasPaid, token]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const url = await payInvoicePublic(token);
      window.location.href = url; // redirect to Stripe Checkout
    } catch (e) {
      setErr(e.message);
      setPaying(false);
    }
  };

  if (loading) {
    return <CenteredMessage><div style={{ color: '#7aa3c0' }}>Loading invoice…</div></CenteredMessage>;
  }
  if (err || !data) {
    return (
      <CenteredMessage>
        <Receipt size={48} style={{ color: '#7aa3c0' }} />
        <h1 style={{ marginTop: 16, color: '#e6f0fa' }}>Invoice not found</h1>
        <p style={{ color: '#7aa3c0', marginTop: 8 }}>This link is invalid or has been removed. Please contact your agent.</p>
      </CenteredMessage>
    );
  }

  const badge = STATUS_BADGE[data.status] || STATUS_BADGE.SENT;
  const overdue = data.dueDate && new Date(data.dueDate) < new Date() && data.balance > 0;

  return (
    <div style={{ minHeight: '100vh', background: '#0a1628', color: '#e6f0fa', padding: '2rem 1.25rem', fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.08em' }}>From</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{data.agencyName}</div>
          </div>
          <span style={{
            padding: '6px 14px', borderRadius: 99,
            background: badge.bg, color: badge.color,
            fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em',
          }}>{badge.label}</span>
        </div>

        {wasPaid && data.status === 'PAID' && (
          <div style={{ background: 'rgba(0,255,136,.08)', border: '1px solid #00ff88', borderRadius: 12, padding: '1rem 1.2rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: 10 }}>
            <CheckCircle size={18} style={{ color: '#00ff88' }} />
            <strong style={{ color: '#00ff88' }}>Payment received — thank you!</strong>
          </div>
        )}

        {overdue && data.status !== 'PAID' && (
          <div style={{ background: 'rgba(255,61,107,.08)', border: '1px solid #ff3d6b', borderRadius: 12, padding: '1rem 1.2rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertCircle size={18} style={{ color: '#ff3d6b' }} />
            <span><strong style={{ color: '#ff3d6b' }}>This invoice is overdue.</strong> Was due on {fmtDate(data.dueDate)}.</span>
          </div>
        )}

        {/* Invoice card */}
        <div style={{ background: '#0d1c2e', border: '1px solid #1a3550', borderRadius: 16, padding: '1.5rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.08em' }}>Invoice</div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{data.number}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.08em' }}>
                <Calendar size={11} style={{ verticalAlign: '-1px' }} /> Issued
              </div>
              <div style={{ fontSize: 14 }}>{fmtDate(data.issueDate)}</div>
              {data.dueDate && (
                <>
                  <div style={{ fontSize: 12, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 8 }}>Due</div>
                  <div style={{ fontSize: 14 }}>{fmtDate(data.dueDate)}</div>
                </>
              )}
            </div>
          </div>

          {data.title && (
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: '1rem' }}>{data.title}</div>
          )}

          {/* Items */}
          <div style={{ borderTop: '1px solid #1a3550', borderBottom: '1px solid #1a3550', padding: '.75rem 0', margin: '0 0 1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 100px 100px', gap: 8, fontSize: 11, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
              <div>Description</div>
              <div style={{ textAlign: 'right' }}>Qty</div>
              <div style={{ textAlign: 'right' }}>Unit</div>
              <div style={{ textAlign: 'right' }}>Total</div>
            </div>
            {data.items.map((it, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 100px 100px', gap: 8, fontSize: 14, padding: '.5rem 0', borderTop: i > 0 ? '1px solid #15293f' : 'none' }}>
                <div>{it.description}</div>
                <div style={{ textAlign: 'right' }}>{it.quantity}</div>
                <div style={{ textAlign: 'right' }}>{fmt$(it.unitPrice)}</div>
                <div style={{ textAlign: 'right', fontWeight: 700 }}>{fmt$(it.total)}</div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', fontSize: 14 }}>
            <Row label="Subtotal" value={fmt$(data.subtotal)} />
            {data.taxRate > 0 && <Row label={`Tax (${(data.taxRate * 100).toFixed(2)}%)`} value={fmt$(data.taxAmount)} />}
            <Row label="Total" value={fmt$(data.total)} bold accent />
            {data.amountPaid > 0 && <Row label="Paid" value={`-${fmt$(data.amountPaid)}`} positive />}
            {data.amountPaid > 0 && <Row label="Balance Due" value={fmt$(data.balance)} bold danger={data.balance > 0} />}
          </div>

          {data.notes && (
            <div style={{ marginTop: '1.25rem', padding: '.75rem 1rem', background: '#15293f', borderRadius: 10, fontSize: 13, color: '#cbd5e1', lineHeight: 1.5 }}>
              <div style={{ fontSize: 11, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Notes</div>
              {data.notes}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: '1rem' }}>
          {data.paymentEnabled && data.balance > 0 && (
            <button
              type="button"
              onClick={handlePay}
              disabled={paying}
              style={{
                flex: 1, minWidth: 200,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '1rem 1.5rem',
                background: paying ? '#475569' : 'linear-gradient(135deg, #00d4ff, #0099cc)',
                color: '#000', fontWeight: 800, fontSize: 16,
                border: 'none', borderRadius: 12,
                cursor: paying ? 'wait' : 'pointer',
                boxShadow: paying ? 'none' : '0 4px 16px rgba(0,212,255,.3)',
              }}
            >
              <CreditCard size={18} />
              {paying ? 'Opening Stripe…' : `Pay ${fmt$(data.balance)}`}
            </button>
          )}
          <a
            href={invoicePdfPublicUrl(token)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '1rem 1.5rem',
              background: 'transparent',
              border: '1px solid #1a3550',
              color: '#e6f0fa', fontWeight: 700, fontSize: 14,
              borderRadius: 12,
              textDecoration: 'none',
            }}
          >
            <Download size={16} />
            Download PDF
          </a>
        </div>

        <div style={{ textAlign: 'center', color: '#45688a', fontSize: 11, marginTop: '2rem' }}>
          Powered by Deskte · {data.agencyName}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, accent, positive, danger }) {
  return (
    <div style={{ display: 'flex', gap: 24, minWidth: 240, justifyContent: 'space-between' }}>
      <span style={{ color: '#7aa3c0', fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{
        fontWeight: bold ? 800 : 400,
        color: danger ? '#ff3d6b' : positive ? '#00ff88' : accent ? '#00d4ff' : '#e6f0fa',
        fontSize: bold ? 16 : 14,
      }}>{value}</span>
    </div>
  );
}

function CenteredMessage({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a1628', color: '#e6f0fa', padding: '2rem' }}>
      <div style={{ textAlign: 'center', maxWidth: 480 }}>{children}</div>
    </div>
  );
}
